import React, { useEffect, useState, useRef } from "react";
import { TileLayer, useMap } from "react-leaflet";

// --- Helper: Date Formatting ---
const toDateKey = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return null;
};

// --- Helper: Coordinate Conversion ---
const mercatorToLatLng = (x, y) => {
  const rMajor = 6378137; 
  const shift = Math.PI * rMajor;
  const lon = (x / shift) * 180.0;
  let lat = (y / shift) * 180.0;
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180.0)) - Math.PI / 2.0);
  return [lat, lon];
};

export default function Raster3dLayer({ latestSurvey, pane, apiBase }) {
  const map = useMap();
  const [layers, setLayers] = useState([]);
  const isCancelledRef = useRef(false);
  const hasZoomedRef = useRef(false);

  // --- Logic: URL Builder ---
  const buildTileUrl = (cand) => {
    let pathPart = "";

    if (cand.tile_url_root) {
      try {
        if (cand.tile_url_root.startsWith("http")) {
          const u = new URL(cand.tile_url_root);
          pathPart = u.pathname; 
        } else {
          pathPart = cand.tile_url_root;
        }
      } catch (e) {
        pathPart = cand.tile_url_root;
      }
    } else {
      // Fallback
      const locSlug = String(latestSurvey.location_name).toLowerCase().replace(/[^a-z0-9-]+/g, '-');
      const surveySlug = toDateKey(latestSurvey.survey_time);
      const layerSlug = cand.layer_slug || cand.layer_name;
      pathPart = `/3draster/${locSlug}/${surveySlug}/${layerSlug}/tiles/`;
    }

    const cleanApiBase = apiBase.replace(/\/+$/, "");
    const cleanPath = pathPart.replace(/^\/+/, "").replace(/\/+$/, "");
    return `${cleanApiBase}/${cleanPath}/{z}/{x}/{y}.png`;
  };

  // --- Logic: Metadata Fetch ---
  const fetchLayerMetadata = async (tileUrlTemplate) => {
    try {
      const xmlUrl = tileUrlTemplate.replace(/\{z\}\/\{x\}\/\{y\}\.png$/, "tilemapresource.xml");
      const resp = await fetch(xmlUrl);
      if (!resp.ok) return null;
      
      const text = await resp.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, "text/xml");
      const bbox = xmlDoc.querySelector("BoundingBox"); 

      if (bbox) {
        const minx = parseFloat(bbox.getAttribute("minx"));
        const miny = parseFloat(bbox.getAttribute("miny"));
        const maxx = parseFloat(bbox.getAttribute("maxx"));
        const maxy = parseFloat(bbox.getAttribute("maxy"));

        let sw, ne;
        if (Math.abs(minx) <= 180 && Math.abs(miny) <= 90) {
            sw = [miny, minx]; 
            ne = [maxy, maxx];
        } else {
            sw = mercatorToLatLng(minx, miny);
            ne = mercatorToLatLng(maxx, maxy);
        }
        return [sw, ne]; 
      }
    } catch (e) { /* ignore */ }
    return null;
  };

  // --- Logic: Main Discovery ---
  const discoverLayers = async () => {
    if (!latestSurvey?.location_name || !latestSurvey?.survey_time) {
      setLayers([]);
      window.dispatchEvent(new CustomEvent("raster3d-layers-discovered", { 
        detail: { layer_names: [], candidates: [] } 
      }));
      return;
    }

    try {
      const params = new URLSearchParams({
        location_name: latestSurvey.location_name,
        survey_time: toDateKey(latestSurvey.survey_time),
      });

      const url = `${apiBase}/layers3d/tiles-discovery?${params.toString()}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error("Discovery failed");
      
      const json = await r.json();
      const foundCandidates = Array.isArray(json.found) ? json.found : [];

      if (isCancelledRef.current) return;

      const processedLayers = await Promise.all(foundCandidates.map(async (cand, index) => {
        
        // --- EXACT FOLDER NAME ---
        // We take the exact name provided by the backend (layer_name is the folder slug)
        const exactFolderName = cand.layer_name || cand.layer_slug || cand.layer_id || `Layer ${index + 1}`;
        
        const uniqueId = `auto-raster3d-${exactFolderName}`; 
        const tileUrl = buildTileUrl(cand);
        const bounds = await fetchLayerMetadata(tileUrl);

        return {
          id: uniqueId,
          name: exactFolderName, // Using the exact folder name string
          url: tileUrl,
          bounds: bounds,
          opacity: 1.0, 
          visible: true
        };
      }));

      setLayers(processedLayers);
      hasZoomedRef.current = false; 

      const layerNamesList = processedLayers.map(l => l.name);
      
      window.dispatchEvent(new CustomEvent("raster3d-layers-discovered", {
        detail: {
          location: latestSurvey.location_name,
          survey_time: latestSurvey.survey_time,
          layer_names: layerNamesList, 
          candidates: foundCandidates
        }
      }));

    } catch (err) {
      console.error("[Raster3D] Error:", err);
      setLayers([]);
    }
  };

  useEffect(() => {
    isCancelledRef.current = false;
    discoverLayers();
    return () => { isCancelledRef.current = true; };
  }, [latestSurvey, apiBase]);

  useEffect(() => {
    if (layers.length > 0 && !hasZoomedRef.current) {
      const layerWithBounds = layers.find(l => l.bounds && l.bounds.length === 2);
      if (layerWithBounds) {
        try {
            map.fitBounds(layerWithBounds.bounds, { padding: [20, 20], maxZoom: 20 });
            hasZoomedRef.current = true;
        } catch(e) {}
      }
    }
  }, [layers, map]);

  useEffect(() => {
    const handleToggle = (ev) => {
      const { id, visible } = ev.detail || {};
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, visible: !!visible } : l));
    };

    const handleOpacity = (ev) => {
      const { id, opacity } = ev.detail || {};
      setLayers(prev => prev.map(l => (l.id === id || l.name === id) ? { ...l, opacity: Number(opacity) } : l));
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-set-opacity", handleOpacity);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-set-opacity", handleOpacity);
    };
  }, []);

  return (
    <>
      {layers.map(layer => (
        layer.visible && (
          <TileLayer
            key={layer.id}
            url={layer.url}
            tms={false} 
            opacity={layer.opacity}
            maxNativeZoom={23}
            maxZoom={24}
            minZoom={0}
            pane={pane}
            eventHandlers={{
              tileerror: (e) => { e.tile.style.display = 'none'; }
            }}
          />
        )
      ))}
    </>
  );
}